/**
 * useDrivers Hook
 * React Query hooks for driver management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { driverService } from '../services/driver.service';
import type {
  DriverStatus,
  CreateDriverRequest,
  AssignRouteRequest,
  AddCertificationRequest,
  UpdateAvailabilityRequest,
  UpdateCDLRequest,
} from '../services/driver.service';

/**
 * Query key factory
 */
const driverKeys = {
  all: ['drivers'] as const,
  lists: () => [...driverKeys.all, 'list'] as const,
  list: (filters?: { status?: DriverStatus; availableDate?: string }) =>
    [...driverKeys.lists(), filters] as const,
  details: () => [...driverKeys.all, 'detail'] as const,
  detail: (id: string) => [...driverKeys.details(), id] as const,
};

/**
 * Hook to list drivers with optional filters
 */
export function useDrivers(filters?: { status?: DriverStatus; availableDate?: string }) {
  return useQuery({
    queryKey: driverKeys.list(filters),
    queryFn: () => driverService.list(filters),
  });
}

/**
 * Hook to get a single driver by ID
 */
export function useDriver(id: string) {
  return useQuery({
    queryKey: driverKeys.detail(id),
    queryFn: () => driverService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a driver
 */
export function useCreateDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDriverRequest) => driverService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
    },
  });
}

/**
 * Hook to update a driver
 */
export function useUpdateDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDriverRequest> }) =>
      driverService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to delete a driver
 */
export function useDeleteDriver() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => driverService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
    },
  });
}

/**
 * Hook to assign a driver to a route
 */
export function useAssignRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, data }: { driverId: string; data: AssignRouteRequest }) =>
      driverService.assignRoute(driverId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
      // Also invalidate vehicles since route assignment affects both
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/**
 * Hook to unassign a driver from a route
 */
export function useUnassignRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (driverId: string) => driverService.unassignRoute(driverId),
    onSuccess: (_, driverId) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(driverId) });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

/**
 * Hook to add a certification
 */
export function useAddCertification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, data }: { driverId: string; data: AddCertificationRequest }) =>
      driverService.addCertification(driverId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
    },
  });
}

/**
 * Hook to delete a certification
 */
export function useDeleteCertification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, certificationId }: { driverId: string; certificationId: string }) =>
      driverService.deleteCertification(driverId, certificationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
    },
  });
}

/**
 * Hook to update availability
 */
export function useUpdateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, data }: { driverId: string; data: UpdateAvailabilityRequest }) =>
      driverService.updateAvailability(driverId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
    },
  });
}

/**
 * Hook to update CDL information
 */
export function useUpdateCDL() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, data }: { driverId: string; data: UpdateCDLRequest }) =>
      driverService.updateCDL(driverId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
    },
  });
}

/**
 * Hook to update driver status
 */
export function useUpdateDriverStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ driverId, status }: { driverId: string; status: DriverStatus }) =>
      driverService.updateStatus(driverId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: driverKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.detail(variables.driverId) });
    },
  });
}
